// One shared authentication snapshot; native sessionStorage remains the legacy
// read-through cache and keeps drafts/navigation state local to each tab.
export const AUTH_SESSION_KEY = "INR_AUTH_SESSION";
export const AUTH_SESSION_KEYS = [
  "TOKEN",
  "LOGIN_DETAILS",
  "USER_ID",
  "ROLE_RIGHTS_LOGIN",
  "ROLE",
  "ROLE_NAME",
  "USER_DETAILS",
  "SELECTED_LOCATION",
  "WIZARD_STATUS_FLAG",
  "PLAN_DETAILS",
  "LOGIN_DATETIME",
  "RESET_PASSWORD",
  "LOGIN_ROUTE",
];
export function sessionUser(storage = window.sessionStorage) {
  try {
    return JSON.parse(storage.getItem("LOGIN_DETAILS") || "null");
  } catch {
    return null;
  }
}
export function hasAuthSession(storage = window.sessionStorage) {
  return Boolean(storage.getItem("TOKEN") && sessionUser(storage)?._id);
}
function identity(data) {
  try {
    return JSON.parse(data?.LOGIN_DETAILS || "null")?._id || null;
  } catch {
    return null;
  }
}
function valid(data) {
  if (!data || typeof data.TOKEN !== "string" || !data.TOKEN || !identity(data))
    return false;
  try {
    const payload = JSON.parse(
      atob(data.TOKEN.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    if (payload.exp && payload.exp * 1000 <= Date.now()) return false;
    if (payload._id && String(payload._id) !== String(identity(data)))
      return false;
  } catch {
    /* The server remains responsible for validating opaque/legacy tokens. */
  }
  return AUTH_SESSION_KEYS.every(
    (key) => data[key] == null || typeof data[key] === "string"
  );
}
export function createAuthSession({ persistent, tab, onChange = () => {} }) {
  const read = () => {
    const raw = persistent.getItem(AUTH_SESSION_KEY);
    if (raw === null) return undefined;
    try {
      const value = JSON.parse(raw);
      return value.version === 1 && valid(value.data) ? value.data : null;
    } catch {
      return null;
    }
  };
  const snapshot = () =>
    Object.fromEntries(
      AUTH_SESSION_KEYS.map((key) => [key, tab.getItem(key)]).filter(
        ([, value]) => value !== null
      )
    );
  const apply = (data) => {
    const before = snapshot();
    const changedAccount = identity(before) !== identity(data);
    if (
      (changedAccount || !data) &&
      (before.TOKEN || identity(before) || identity(data))
    )
      tab.clear();
    AUTH_SESSION_KEYS.forEach((key) => {
      if (data?.[key] != null) tab.setItem(key, data[key]);
      else tab.removeItem(key);
    });
    return (
      changedAccount ||
      before.ROLE_RIGHTS_LOGIN !== data?.ROLE_RIGHTS_LOGIN ||
      before.ROLE !== data?.ROLE
    );
  };
  const save = () => {
    const data = snapshot();
    if (!valid(data)) return false;
    persistent.setItem(AUTH_SESSION_KEY, JSON.stringify({ version: 1, data }));
    return true;
  };
  const clear = () => {
    persistent.setItem(
      AUTH_SESSION_KEY,
      JSON.stringify({ version: 1, data: null })
    );
    persistent.removeItem("userPassword");
    persistent.removeItem("persist:root");
    tab.clear();
  };
  return {
    save,
    clear,
    initialize() {
      // Migrate an already-open tab once, but never revive a logged-out snapshot.
      const data = read();
      if (data === undefined && valid(snapshot())) save();
      else apply(data || null);
      if (data === null)
        persistent.setItem(
          AUTH_SESSION_KEY,
          JSON.stringify({ version: 1, data: null })
        );
      persistent.removeItem("userPassword");
    },
    synchronize() {
      const data = read();
      const reload = apply(data || null);
      onChange({ reload, authenticated: Boolean(data) });
    },
    setItem(key, value) {
      tab.setItem(key, value);
      const saved = read();
      // Login builds metadata before committing its token. Never publish a mixed
      // old-token/new-account snapshot during an account switch.
      if (
        key === "TOKEN" ||
        (saved &&
          identity(saved) === identity(snapshot()) &&
          saved.TOKEN === tab.getItem("TOKEN"))
      )
        save();
    },
  };
}
let browserSession;
function current() {
  if (!browserSession)
    browserSession = createAuthSession({
      persistent: window.localStorage,
      tab: window.sessionStorage,
    });
  return browserSession;
}
export function setAuthSessionItem(key, value) {
  current().setItem(key, value);
}
export function persistAuthSession() {
  return current().save();
}
export function clearAuthSession() {
  current().clear();
}
